import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';

/**
 * Card de cancelamento em LOTE (`voucher_cancellation_bulk`) — lista os vouchers
 * canceláveis do viajante com checkbox; o cliente marca os que quer cancelar e
 * SUBMETE de uma vez. Espelha o guardrail do `voucher_cancellation` (single):
 * UM challenge de sessão emitido pelo agente cobre o SUBMIT inteiro; os códigos
 * marcados viajam FORA do texto livre em `interaction.data` → `confirmation.
 * selections` (o mesmo canal opaco do `booking_approval`), validados server-side
 * contra `VoucherBulkSelectionsSchema`. O servidor re-resolve e re-avalia CADA
 * voucher (escopo do próprio viajante via JWT) antes de cancelar — o card é
 * DISPLAY-ONLY, não autoriza nada sozinho.
 *
 * Só entram vouchers CANCELÁVEIS (o agente filtra pelo veredito determinístico);
 * `postEmission` marca os EMITIDOS, cujo cancelamento vira uma SOLICITAÇÃO
 * (revisão manual + marca `não utilizado`).
 */

/** Um item selecionável — projeção enxuta do voucher (sem id interno). */
const VoucherCancellationBulkItemSchema = z.object({
  voucherCode: z.string(),
  modal: z.string().optional(),
  /** Trecho (origem → destino) ou Local (estadia) — a UI decide o rótulo. */
  route: z.string().optional(),
  status: z.string().optional(),
  travelDate: z.string().optional(),
  /** Voucher emitido → o cancelamento é uma solicitação (revisão manual). */
  postEmission: z.boolean().optional(),
});
export type VoucherCancellationBulkItem = z.infer<typeof VoucherCancellationBulkItemSchema>;

/** Teto do produtor — limita o frame SSE de um viajante com muitos vouchers. */
export const CHAT_UI_MAX_BULK_VOUCHERS = 30;

/**
 * Seleção OPACA ecoada no submit (`confirmation.selections`). O shell repassa o
 * `interaction.data` sem interpretar; o handler do agents valida contra este
 * schema antes de agir. Os `voucherCodes` são a escolha do cliente, mas NÃO
 * autorizam sozinhos: o `ref` do challenge confirmado limita o conjunto elegível
 * e cada voucher é re-avaliado server-side.
 */
export const VoucherBulkSelectionsSchema = z
  .object({
    voucherCodes: z.array(z.string().min(1)).min(1).max(CHAT_UI_MAX_BULK_VOUCHERS),
  })
  .strict();
export type VoucherBulkSelections = z.infer<typeof VoucherBulkSelectionsSchema>;

/** Membro `voucher_cancellation_bulk` do `ChatUiSchema` (discriminatedUnion). */
export const VoucherCancellationBulkBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('voucher_cancellation_bulk'),
    // Challenge de sessão emitido pelo agente — cobre o SUBMIT inteiro. O schema
    // exige (`min(1)`) para um card desarmado falhar a validação e ser dropado.
    challengeId: z.string().min(1),
    expiresInMin: z.number().optional(),
    vouchers: z.array(VoucherCancellationBulkItemSchema).min(1).max(CHAT_UI_MAX_BULK_VOUCHERS),
  })
  .strict();
export type VoucherCancellationBulkBlock = z.infer<typeof VoucherCancellationBulkBlockSchema>;
